import { AxiosError } from "axios";
import { toast, ToastOptions, TypeOptions } from "react-toastify";

/*
 Il messaggio da mostrare all'operatore quando una chiamata fallisce.

 Su un errore di axios ci sono due `message`, e per anni e' stato letto quello sbagliato:

   - `error.message` e' la descrizione che axios da' del fallimento HTTP: «Request failed with
     status code 400», sempre in inglese e sempre tecnica;
   - `error.response.data.message` e' il corpo della risposta, cioe' la frase che il backend ha
     scritto apposta per chi legge.

 Il difetto e' rimasto invisibile finche' il foglio di stile di react-toastify non era importato e
 nessun avviso compariva a schermo. Con gli avvisi visibili si vedrebbe la stringa inglese di
 axios. C'era anche una seconda conseguenza, piu' insidiosa: `error.message` non e' mai vuota,
 quindi la ricaduta italiana scritta dopo `||` era codice morto e non e' mai comparsa a nessuno.

 Qui si legge il campo giusto e si ricade sulla frase italiana solo quando il backend non ne ha
 mandata una — perche' non ha risposto affatto, per esempio, o perche' e' caduta la rete.
*/
export function messaggioDiErrore(errore: unknown, ricaduta: string): string {
  const dalBackend = (errore as AxiosError<{ message?: string }>)?.response?.data
    ?.message;
  return dalBackend && dalBackend.trim() ? dalBackend : ricaduta;
}

/*
 Un avviso che non si impila su se stesso.

 Gli avvisi di react-toastify sono indipendenti l'uno dall'altro: due tentativi di salvataggio
 producono due avvisi, e il primo resta a schermo anche quando il secondo lo smentisce. E' il
 caso peggiore da leggere, perche' l'operatore si ritrova l'errore appena corretto accanto alla
 conferma che l'ha superato e non sa quale dei due vale.

 `avvisoUnico` lega l'avviso a un'identita' — «l'esito del salvataggio del dipendente», non «un
 messaggio» — e di quell'identita' a schermo ce n'e' sempre e solo l'ultima: se un avviso con
 quell'id e' ancora aperto lo riscrive (testo e colore), altrimenti ne apre uno nuovo. Il
 riscrittura e' necessaria perche' `toast` con un `toastId` gia' attivo viene scartato in
 silenzio, e a schermo resterebbe il messaggio vecchio.
*/
export function avvisoUnico(
  id: string,
  tipo: TypeOptions,
  testo: string,
  opzioni: ToastOptions = {}
): void {
  if (toast.isActive(id)) {
    toast.update(id, {
      render: testo,
      type: tipo,
      autoClose: 5000,
      ...opzioni,
    });
    return;
  }
  toast(testo, { toastId: id, type: tipo, ...opzioni });
}
